import { CYCLE_PAGE_LAYOUT } from '@amelio/ui-library';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';

import { FormNavigatorDefaultFooter } from './FormNavigatorDefaultFooter';
import { FormNavigatorExitAction } from './FormNavigatorExitAction';
import { FormNavigatorHostedFooter } from './FormNavigatorHostedFooter';
import { FormNavigatorPageHeader } from './FormNavigatorPageHeader';
import { StepFooterProvider } from './StepFooter';

import type { FormNavigatorProps, ValidStepId } from './types';
import type { Step } from '@/components/Stepper/StepItem';

import { Stepper } from '@/components/Stepper/Stepper';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';

interface GetStepStatusParams {
    isCompleted: boolean;
    isStepInvalid: boolean;
}

const getStepStatus = ({ isCompleted, isStepInvalid }: GetStepStatusParams): Step['status'] => {
    if (!isCompleted) {
        return undefined;
    }

    return isStepInvalid ? 'error' : 'completed';
};

export const FormNavigator = ({
    steps,
    routePrefix,
    urlParamName,
    getStepFromPath,
    getStepPath,
    backRoute,
    onSuccess,
    onNextCallbacks,
    lastSavedAt,
    stepperTitle,
    stepperSubtitle,
    stepperDataTestId,
    routes,
    // The sticky offset is in real pixels, as in `EnvelopeWorkspace`: the rail hangs below the
    // host's fixed 64px header, a height the module does not own, so it cannot ride the module's
    // own spacing scale — a utility here is a multiple of `--spacing`, and the rail slides away
    // from that header every time the scale is retuned. 91px clears the header by 27px.
    stepperInnerClassName = 'w-full lg:sticky lg:top-[91px] lg:!w-80',
    contentClassName,
    initialMaxStep,
    isLaunched,
    onExit,
    getStepAriaLabel,
    stepperVariant = 'card',
    getStepRankLabel,
    readOnlyNotice,
    pageTitle,
    pageSubtitle,
    stepsHostingFooter,
    nextBlockedByStep,
}: FormNavigatorProps) => {
    const params = useParams<Record<string, string>>();
    const step = params.step;
    const entityId = params[urlParamName];
    const navigate = useNavigate();
    const { handleError } = useError();

    const [isLoading, setIsLoading] = useState(false);

    const isReadOnly = readOnlyNotice !== undefined;

    const [currentStep, setCurrentStep] = useState(() => {
        const stepId = getStepFromPath(step);
        const index = steps.findIndex((s) => s.id === stepId);
        return index >= 0 ? index : 0;
    });

    const [maxReachedStep, setMaxReachedStep] = useState(
        initialMaxStep !== undefined ? Math.max(currentStep, initialMaxStep) : currentStep,
    );

    const prevStepsLengthRef = useRef(steps.length);

    // Ref stays current across async suspensions — the closure would capture stale `steps`.
    const stepsRef = useRef(steps);

    useEffect(() => {
        stepsRef.current = steps;
    }, [steps]);

    const stepsWithStatus: Step[] = useMemo(
        () =>
            steps.map((s, index) => {
                const isLastStep = index === steps.length - 1;
                const isCompleted =
                    index <= maxReachedStep &&
                    index !== currentStep &&
                    !(isLastStep && !isLaunched);
                const isStepInvalid = Boolean(nextBlockedByStep?.[s.id as ValidStepId]);

                return {
                    ...s,
                    status: getStepStatus({ isCompleted, isStepInvalid }),
                    disabled: index > maxReachedStep,
                };
            }),
        [steps, maxReachedStep, currentStep, isLaunched, nextBlockedByStep],
    );

    const onNextAction = async (stepId: ValidStepId): Promise<boolean> => {
        // A read-only cycle has nothing to persist or validate: every step's callback backstops
        // writes by returning false, which would block browsing forward along with saving.
        if (isReadOnly) {
            return true;
        }

        setIsLoading(true);

        try {
            const callback = onNextCallbacks[stepId];
            return (await callback?.()) ?? true;
        } catch (error) {
            handleError({ error });
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        if (currentStep === 0) {
            navigate({ path: backRoute });
            return;
        }

        const previousStep = steps[currentStep - 1];
        setCurrentStep((prev) => prev - 1);
        navigate({
            path: `${routePrefix}/${getStepPath(previousStep.id)}/${entityId}`,
        });
    };

    const handleStepClick = async (clickedStep: Step, index: number) => {
        if (isLoading || index > maxReachedStep || index === currentStep) {
            return;
        }

        if (index > currentStep) {
            if (!isReadOnly) {
                for (let stepIndex = currentStep; stepIndex < index; stepIndex++) {
                    const stepId = stepsRef.current[stepIndex].id as ValidStepId;
                    if (nextBlockedByStep?.[stepId]) {
                        return;
                    }
                }
            }

            const currentStepId = stepsRef.current[currentStep].id as ValidStepId;
            const isValid = await onNextAction(currentStepId);

            if (!isValid) {
                return;
            }
        }

        setCurrentStep(index);
        setMaxReachedStep((prev) => Math.max(prev, index));
        navigate({
            path: `${routePrefix}/${getStepPath(clickedStep.id)}/${entityId}`,
        });
    };

    const handleNext = async () => {
        const currentStepId = stepsRef.current[currentStep].id as ValidStepId;
        const isValid = await onNextAction(currentStepId);

        if (!isValid) {
            return;
        }

        const latestSteps = stepsRef.current;

        if (currentStep < latestSteps.length - 1) {
            const nextStep = latestSteps[currentStep + 1];
            setCurrentStep((prev) => prev + 1);
            setMaxReachedStep((prev) => Math.max(prev, currentStep + 1));
            navigate({
                path: `${routePrefix}/${getStepPath(nextStep.id)}/${entityId}`,
            });

            return;
        }

        onSuccess();
    };

    useEffect(() => {
        if (initialMaxStep !== undefined) {
            setMaxReachedStep((prev) => Math.max(prev, initialMaxStep));
        }
    }, [initialMaxStep]);

    useEffect(() => {
        const stepId = getStepFromPath(step);
        const index = steps.findIndex((s) => s.id === stepId);
        const stepsShrank = steps.length < prevStepsLengthRef.current;
        const stepsGrew = steps.length > prevStepsLengthRef.current;
        prevStepsLengthRef.current = steps.length;

        if (index >= 0 && index !== currentStep) {
            setCurrentStep(index);
            if (stepsShrank) {
                setMaxReachedStep(initialMaxStep !== undefined ? initialMaxStep : index);
            }
        } else if (stepsShrank) {
            setMaxReachedStep((prev) => {
                const adjusted =
                    initialMaxStep !== undefined ? Math.max(prev, initialMaxStep) : prev;
                return Math.min(adjusted, steps.length - 1);
            });
        }

        if (stepsGrew && initialMaxStep !== undefined) {
            setMaxReachedStep(initialMaxStep);
        }
    }, [step, entityId, steps, currentStep, initialMaxStep]);

    const draftStepIndex = steps.findIndex(
        (s) => s.id === CycleInstanceSteps.QUESTIONNAIRE_CREATION,
    );

    const isRail = stepperVariant === 'rail';

    const isFirstStep = currentStep === 0;
    const isNextAvailable = currentStep < steps.length - 1;

    const isNextBlocked =
        !isReadOnly && Boolean(nextBlockedByStep?.[steps[currentStep]?.id as ValidStepId]);

    const isFooterHostedByStep = Boolean(
        stepsHostingFooter?.includes(steps[currentStep]?.id as ValidStepId),
    );

    const hostedFooter = (
        <FormNavigatorHostedFooter
            onBack={handleBack}
            onNext={handleNext}
            isNextAvailable={isNextAvailable}
            isLoading={isLoading}
            isNextDisabled={isNextBlocked}
        />
    );

    const showDraftIndicator = draftStepIndex >= 0 && currentStep === draftStepIndex;

    return (
        <div
            className={
                isRail
                    ? [
                          CYCLE_PAGE_LAYOUT.maxWidth,
                          'cycle-page-top-gap',
                          'stepper-layout mx-auto flex w-full flex-col',
                      ].join(' ')
                    : 'stepper-layout w-full pt-8 lg:flex lg:flex-row lg:pl-5'
            }
        >
            {isRail && pageTitle && (
                <FormNavigatorPageHeader
                    pageTitle={pageTitle}
                    pageSubtitle={pageSubtitle}
                    notice={readOnlyNotice}
                />
            )}

            <div
                className={
                    isRail
                        ? 'cycle-rail-gutter'
                        : 'mx-auto shrink-0 px-4 pb-8 sm:!px-12 lg:w-80 lg:px-0'
                }
            >
                <div className={isRail ? '' : stepperInnerClassName}>
                    <Stepper
                        steps={stepsWithStatus}
                        currentStep={currentStep}
                        orientation={'vertical'}
                        variant={stepperVariant}
                        size={'md'}
                        title={stepperTitle}
                        subtitle={stepperSubtitle}
                        onStepClick={handleStepClick}
                        getStepAriaLabel={getStepAriaLabel}
                        getStepRankLabel={getStepRankLabel}
                        dataTestId={stepperDataTestId}
                    />
                </div>
            </div>

            <div className={'flex-1'}>
                <div
                    className={[
                        contentClassName,
                        isRail ? 'cycle-evaluation-group-step-content' : 'sm:px-8',
                        'mx-auto flex flex-col justify-between pb-8',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    {onExit && !isFirstStep && <FormNavigatorExitAction onExit={onExit} />}

                    <div className={'relative z-10'}>
                        <StepFooterProvider value={isFooterHostedByStep ? hostedFooter : null}>
                            <Switch>
                                {routes.map(({ path, component: Component }) => (
                                    <Route
                                        key={path}
                                        exact
                                        path={path}
                                        component={Component}
                                    />
                                ))}
                            </Switch>
                        </StepFooterProvider>
                    </div>

                    {!isFooterHostedByStep && (
                        <FormNavigatorDefaultFooter
                            showDraftIndicator={showDraftIndicator}
                            lastSavedAt={lastSavedAt}
                            onBack={handleBack}
                            onNext={handleNext}
                            isNextAvailable={isNextAvailable}
                            isLoading={isLoading}
                            isNextDisabled={isNextBlocked}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
